import dayjs from 'dayjs'
import {Button} from '@/components/ui/button/Button'
import {AlertCircleIcon, ArrowLeftIcon, Copy01Icon, Download01Icon} from '@/components/ui/icon/Icon'
import {Spinner} from '@/components/ui/spinner/Spinner'
import {Checkbox} from '@/components/commons/checkbox/ChekBox'
import {useTranslation} from '@/translations/i18n'
import {patientServiceTypeMapToLabel} from '@/utilities/helpers'
import {toast} from '@/utilities/toast/toast'
import {SiarExportPreviewItem} from '@/features/projects/siar-bulk-export/types'
import {HttpGetSiarBulkExportPreviewCountersResponse} from '@/features/projects/siar-bulk-export/http/siarBulkExport.http'
import {BulkExportConfig} from '@/features/projects/siar-bulk-export/components/bulk-siar-export-dialog/BulkSiarExportConfigStep'
import {PreviewFilterTab} from '@/features/projects/siar-bulk-export/components/bulk-siar-export-dialog/BulkSiarExportDialog'
import {BulkSiarExportCriteriaSummary} from '@/features/projects/siar-bulk-export/components/bulk-siar-export-dialog/BulkSiarExportCriteriaSummary'
import {BulkSiarExportPreviewTable} from '@/features/projects/siar-bulk-export/components/bulk-siar-export-dialog/BulkSiarExportPreviewTable'
import {BulkSiarExportPreviewTableSkeleton} from '@/features/projects/siar-bulk-export/components/bulk-siar-export-dialog/BulkSiarExportPreviewTableSkeleton'
import {BulkSiarExportInOtherBatchBanner} from '@/features/projects/siar-bulk-export/components/bulk-siar-export-dialog/BulkSiarExportInOtherBatchBanner'
import {
    StyledBulkSiarExportBackButton,
    StyledBulkSiarExportExcludedBanner,
    StyledBulkSiarExportExcludedBannerText,
    StyledBulkSiarExportExcludedTabIcon,
    StyledBulkSiarExportFilterTab,
    StyledBulkSiarExportFilterTabBar,
    StyledBulkSiarExportOpeningFilterRow,
    StyledBulkSiarExportPreviewBody,
    StyledBulkSiarExportPreviewCounterRow,
    StyledBulkSiarExportPreviewEmptyState,
    StyledBulkSiarExportPreviewFooter,
    StyledBulkSiarExportToggleAllButton
} from '@/features/projects/siar-bulk-export/components/bulk-siar-export-dialog/style'

type BulkSiarExportPreviewStepProps = {
    config: BulkExportConfig
    items: SiarExportPreviewItem[]
    total: number
    isLoading: boolean
    counters: HttpGetSiarBulkExportPreviewCountersResponse | undefined
    isCountersLoading: boolean
    activeTab: PreviewFilterTab
    onTabChange: (tab: PreviewFilterTab) => void
    onlyOpeningTransmitted: boolean
    onOnlyOpeningTransmittedChange: (value: boolean) => void
    isSelected: (item: SiarExportPreviewItem) => boolean
    selectedCount: number
    viewSelectedCount: number
    viewEligibleTotal: number
    onMasterToggle: () => void
    onToggle: (item: SiarExportPreviewItem) => void
    onIncludeOtherBatch: () => void
    onIgnoreOtherBatch: () => void
    selectedInOtherBatchCount: number
    onLoadMore: () => void
    onBack: () => void
    onExport: () => void
    isExportPending: boolean
}

export const BulkSiarExportPreviewStep = ({
    config,
    items,
    isLoading,
    counters,
    isCountersLoading,
    activeTab,
    onTabChange,
    onlyOpeningTransmitted,
    onOnlyOpeningTransmittedChange,
    isSelected,
    selectedCount,
    viewSelectedCount,
    viewEligibleTotal,
    onMasterToggle,
    onToggle,
    onIncludeOtherBatch,
    onIgnoreOtherBatch,
    selectedInOtherBatchCount,
    onLoadMore,
    onBack,
    onExport,
    isExportPending
}: BulkSiarExportPreviewStepProps) => {
    const {t} = useTranslation()

    const allDeselected = viewSelectedCount === 0

    // Master checkbox reflects loaded items only
    const loadedSelectedCount = items.filter(isSelected).length
    const masterChecked = items.length > 0 && loadedSelectedCount === items.length
    const masterIndeterminate = loadedSelectedCount > 0 && loadedSelectedCount < items.length

    const isInitialLoading = isLoading && items.length === 0
    const showCounters = !isCountersLoading && !!counters
    const showBanner = selectedInOtherBatchCount > 0
    // The "only opening transmitted" filter is shown only for closing exports.
    const isClosingExport = config.exportType === 'close_project'
    // The "Excluded" tab/view exists only for opening exports (nothing is excluded on closing).
    const isOpeningExport = config.exportType === 'open_project'
    const isExcludedView = activeTab === 'excluded'

    // Copy every excluded project currently shown in the list: one PRI per line, with its
    // info comma-separated — code (no "PRI-" prefix), patient name, service type, start date.
    const handleCopyExcludedIds = () => {
        const text = items
            .map(item => {
                const code = String(item.projectCode).padStart(4, '0')
                const serviceType = item.serviceType ? t(patientServiceTypeMapToLabel[item.serviceType]) : '—'
                const startDate = item.startDate ? dayjs(item.startDate).format('DD/MM/YYYY') : '—'
                return [code, item.patientFullName, serviceType, startDate].join(', ')
            })
            .join('\n')
        navigator.clipboard
            .writeText(text)
            .then(() => toast.success(t('projects:bulk_siar_export_excluded_copy_success')))
            .catch(() => toast.error(t('errors:default')))
    }

    return (
        <>
            <StyledBulkSiarExportPreviewBody>
                <BulkSiarExportCriteriaSummary config={config} />
                {showBanner && (
                    <BulkSiarExportInOtherBatchBanner
                        count={selectedInOtherBatchCount}
                        onInclude={onIncludeOtherBatch}
                        onIgnore={onIgnoreOtherBatch}
                    />
                )}
                {showCounters && !isExcludedView && (
                    <StyledBulkSiarExportPreviewCounterRow>
                        <span>
                            {t('projects:bulk_siar_export_selected_counter', {
                                selected: viewSelectedCount,
                                total: viewEligibleTotal
                            })}
                        </span>
                        {viewEligibleTotal > 0 && (
                            <StyledBulkSiarExportToggleAllButton type="button" onClick={onMasterToggle}>
                                {allDeselected
                                    ? t('projects:bulk_siar_export_select_all')
                                    : t('projects:bulk_siar_export_deselect_all')}
                            </StyledBulkSiarExportToggleAllButton>
                        )}
                    </StyledBulkSiarExportPreviewCounterRow>
                )}
                <StyledBulkSiarExportFilterTabBar>
                    <StyledBulkSiarExportFilterTab
                        type="button"
                        $active={activeTab === 'all'}
                        onClick={() => onTabChange('all')}
                    >
                        {t('projects:bulk_siar_export_preview_tab_all')}
                        {showCounters && ` (${counters.total})`}
                    </StyledBulkSiarExportFilterTab>
                    <StyledBulkSiarExportFilterTab
                        type="button"
                        $active={activeTab === 'to_print'}
                        onClick={() => onTabChange('to_print')}
                    >
                        {t('projects:bulk_siar_export_preview_tab_to_print')}
                        {showCounters && ` (${counters.toPrint})`}
                    </StyledBulkSiarExportFilterTab>
                    <StyledBulkSiarExportFilterTab
                        type="button"
                        $active={activeTab === 'to_export'}
                        onClick={() => onTabChange('to_export')}
                    >
                        {t('projects:bulk_siar_export_preview_tab_to_export')}
                        {showCounters && ` (${counters.toExport})`}
                    </StyledBulkSiarExportFilterTab>
                    <StyledBulkSiarExportFilterTab
                        type="button"
                        $active={activeTab === 'to_transmit'}
                        onClick={() => onTabChange('to_transmit')}
                    >
                        {t('projects:bulk_siar_export_preview_tab_to_transmit')}
                        {showCounters && ` (${counters.toTransmit})`}
                    </StyledBulkSiarExportFilterTab>
                    {isOpeningExport && (
                        <StyledBulkSiarExportFilterTab
                            type="button"
                            $active={isExcludedView}
                            $danger
                            onClick={() => onTabChange('excluded')}
                        >
                            <StyledBulkSiarExportExcludedTabIcon>
                                <AlertCircleIcon size={14} />
                            </StyledBulkSiarExportExcludedTabIcon>
                            {t('projects:bulk_siar_export_preview_tab_excluded')}
                            {showCounters && ` (${counters.excluded})`}
                        </StyledBulkSiarExportFilterTab>
                    )}
                </StyledBulkSiarExportFilterTabBar>
                {isClosingExport && (
                    <StyledBulkSiarExportOpeningFilterRow>
                        <Checkbox
                            id="bulk-siar-export-only-opening-transmitted"
                            label={t('projects:bulk_siar_export_only_opening_transmitted')}
                            checked={onlyOpeningTransmitted}
                            onChange={() => onOnlyOpeningTransmittedChange(!onlyOpeningTransmitted)}
                        />
                    </StyledBulkSiarExportOpeningFilterRow>
                )}
                {isExcludedView && items.length > 0 && (
                    <StyledBulkSiarExportExcludedBanner>
                        <StyledBulkSiarExportExcludedBannerText>
                            <AlertCircleIcon size={18} />
                            <span>{t('projects:bulk_siar_export_excluded_banner')}</span>
                        </StyledBulkSiarExportExcludedBannerText>
                        <Button
                            variant="secondaryDanger"
                            size="sm"
                            onClick={handleCopyExcludedIds}
                            style={{flexShrink: 0}}
                        >
                            <Copy01Icon size={16} />
                            {t('projects:bulk_siar_export_excluded_copy_ids')}
                        </Button>
                    </StyledBulkSiarExportExcludedBanner>
                )}
                {isInitialLoading ? (
                    <BulkSiarExportPreviewTableSkeleton />
                ) : items.length === 0 ? (
                    <StyledBulkSiarExportPreviewEmptyState>
                        {t('projects:bulk_siar_export_preview_empty')}
                    </StyledBulkSiarExportPreviewEmptyState>
                ) : (
                    <BulkSiarExportPreviewTable
                        items={items}
                        isSelected={isSelected}
                        onToggle={onToggle}
                        onToggleAll={onMasterToggle}
                        masterChecked={masterChecked}
                        masterIndeterminate={masterIndeterminate}
                        onLoadMore={onLoadMore}
                        readOnly={isExcludedView}
                    />
                )}
            </StyledBulkSiarExportPreviewBody>
            <StyledBulkSiarExportPreviewFooter>
                <StyledBulkSiarExportBackButton type="button" onClick={onBack}>
                    <ArrowLeftIcon />
                    {t('projects:bulk_siar_export_back')}
                </StyledBulkSiarExportBackButton>
                <Button variant="primaryNeutral" onClick={onExport} disabled={selectedCount === 0 || isExportPending}>
                    <Download01Icon />
                    {t('projects:bulk_siar_export_export_cta', {count: selectedCount})}
                    {isExportPending && <Spinner />}
                </Button>
            </StyledBulkSiarExportPreviewFooter>
        </>
    )
}
